// Pure transformer for the new "Map columns + set defaults" step.
//
// Inputs:
//   - raw: { headers, rows } from parseXlsxFileRaw — XLS cells +
//     hyperlinks keyed by the AM's actual column names, no aliasing.
//   - mapping: SystemField → XLS column header, as picked by the AM
//     in Table 1.
//   - bulkSet: enum-field values picked by the AM in Table 2. Applied
//     uniformly to every row.
//
// Outputs:
//   - ParseResult — same shape the existing preview + import flow
//     already consumes. Empty `missingColumns` because Table-1
//     mapping validation is the step's own gate.
//
// The replay reuses parseRowsFromSheet's per-cell readers (placeholder
// detection, hyperlink-vs-text fallback, FB Ad Account dual-purpose,
// money parsing) so the new flow's parse semantics match the legacy
// auto-alias path exactly. Difference: the AM's mapping wins instead
// of COLUMN_ALIASES.
//
// Pure: no I/O, no DOM.

import { parseRowsFromSheet, type ParseResult, type RawParseResult } from './xlsxParse';
import type { SystemField } from './autoMatchColumn';
import type {
  FormType,
  LocationDefaultMetaAdType,
} from '../../types/franchiseLocationProfile';

// Mapping shape: each entry pairs a SystemField with the XLS column
// header it should read from. 'skip' (or absent key) means "leave
// blank / use field default". The component layer stores its UI
// state keyed by XLS header for ergonomics with the dropdown rows;
// invertMapping below flips into the SystemField → header shape that
// parseRowsFromSheet expects.
export type ColumnMapping = Partial<Record<SystemField, string>>;

// Bulk-set values for the enum fields exposed in Table 2. Each
// undefined entry leaves the field at its parser-default (null,
// which toImportRow then defaults further as appropriate).
export type BulkSetValues = {
  defaultFormType?: FormType;
  defaultMetaAdType?: 'traffic' | 'lead_gen';
};

export function applyMappingAndBulkSet(
  raw: RawParseResult,
  mapping: ColumnMapping,
  bulkSet: BulkSetValues,
): ParseResult {
  // Empty sheet: short-circuit so we don't paper over genuinely
  // empty input with bulk-set values.
  if (raw.rows.length === 0) {
    return { rows: [], warnings: ['Sheet is empty.'], missingColumns: [], headerMap: {} };
  }

  // parseRowsFromSheet's per-row body keys reads off
  // `fieldToHeader[field]`. Our `mapping` already has that shape
  // — `mapping['franchiseSuburb']` is the user-picked XLS header.
  // Pass it through as the override so the legacy auto-alias scan
  // is bypassed.
  const result = parseRowsFromSheet(
    raw.rows.map((r) => r.values),
    raw.headers,
    raw.rows.map((r) => r.hyperlinks),
    { fieldToHeader: mapping },
  );

  // Apply Table-2 bulk-set values uniformly across every row. Each
  // value overrides ParsedRow's parser-default (null) only when the
  // AM picked something — undefined fields leave the parser's null
  // in place, which toImportRow then maps to the historical
  // defaults (Generic / null).
  const setForm: FormType | undefined = bulkSet.defaultFormType;
  const setAdType: LocationDefaultMetaAdType =
    bulkSet.defaultMetaAdType ?? null;
  const enriched = result.rows.map((row) => ({
    ...row,
    defaultFormType: setForm ?? row.defaultFormType,
    defaultMetaAdType: bulkSet.defaultMetaAdType ? setAdType : row.defaultMetaAdType,
  }));

  return { ...result, rows: enriched };
}

// Convenience helper for the component layer: the UI state often
// lives as `Record<XlsHeader, SystemField | 'skip'>` (one entry per
// column row in Table 1, keyed by the original sheet header).
// applyMappingAndBulkSet wants the inverse — SystemField → XLS
// header. Build it here so call sites don't repeat the loop.
//
// Conflicts (same SystemField mapped to multiple headers) take the
// LAST entry, matching the Table-1 "same-field-picked-twice"
// last-wins-with-silent-revert rule. In practice the component
// enforces uniqueness before invoking this helper so the last-wins
// behaviour is defence-in-depth only.
export function invertColumnMapping(
  perHeader: Record<string, SystemField | 'skip'>,
): ColumnMapping {
  const out: ColumnMapping = {};
  for (const [header, field] of Object.entries(perHeader)) {
    if (field === 'skip') continue;
    out[field] = header;
  }
  return out;
}
